import { Controller } from "@hotwired/stimulus"
import Sortable from 'sortablejs';

export default class extends Controller {
  static values = {
    organizationId: Number
  }

  connect() {
    this.initializeSortables();
    this.initializeDropdowns();
    this.setupEscapeHandler();
    this.isDragging = false;
    this.lastHoveredFolder = null;
  }

  disconnect() {
    if (this.sortables) {
      this.sortables.forEach(sortable => sortable.destroy());
    }
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler);
    }
    if (this.expandTimeout) {
      clearTimeout(this.expandTimeout);
    }
    this.cleanupDragState();
  }

  initializeSortables() {
    this.sortables = [];

    // First, temporarily show all folder children to ensure sortables are initialized
    const allFolderChildren = this.element.querySelectorAll('.folder-children');
    const originalDisplayStates = [];
    allFolderChildren.forEach(children => {
      originalDisplayStates.push(children.style.display);
      children.style.display = 'block';
    });

    // Find all sortable containers (including nested ones)
    const containers = this.element.querySelectorAll('[data-sortable-container="true"]');

    containers.forEach((container) => {
      const sortable = new Sortable(container, {
        group: {
          name: 'nested-pages',
          put: true,
          pull: true
        },
        animation: 200,
        easing: "cubic-bezier(0.4, 0.0, 0.2, 1)",
        fallbackOnBody: true,
        swapThreshold: 0.5,
        handle: '.page-card-drag-handle',
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen',
        dragClass: 'sortable-drag',
        direction: 'vertical',
        removeCloneOnHide: true,
        revertOnSpill: true,
        scroll: true,
        scrollSensitivity: 60,
        scrollSpeed: 15,
        bubbleScroll: true,

        onStart: (evt) => {
          this.isDragging = true;
          document.body.classList.add('is-dragging');
          this.clearDragOverClasses();
        },

        onEnd: (evt) => {
          this.isDragging = false;
          try {
            this.handleDrop(evt);
          } finally {
            this.cleanupDragState();
          }
        },

        onMove: (evt) => {
          // Throttle onMove processing
          if (this.moveTimeout) return true;

          this.moveTimeout = setTimeout(() => {
            this.moveTimeout = null;
          }, 50);

          // Clear previous highlights efficiently
          if (this.lastHoveredFolder && this.lastHoveredFolder !== evt.related) {
            this.lastHoveredFolder.classList.remove('drag-over');
          }

          // Check if moving into a folder's children area
          const toContainer = evt.to;
          const folderId = toContainer.dataset.folderId;
          const draggedItem = evt.dragged;

          // Find the folder being dragged over
          let relatedFolder = null;
          if (folderId) {
            relatedFolder = this.element.querySelector(`.folder-card[data-id="${folderId}"]`);
          }

          // Also check if hovering directly over a folder card
          const related = evt.related;
          if (related) {
            if (related.classList.contains('folder-card')) {
              relatedFolder = related;
            } else {
              const parentFolderCard = related.closest('.folder-card');
              if (parentFolderCard && !relatedFolder) {
                relatedFolder = parentFolderCard;
              }
            }
          }

          // Highlight the folder being dragged over
          if (relatedFolder) {
            relatedFolder.classList.add('drag-over');
            this.lastHoveredFolder = relatedFolder;

            // Auto-expand folder if closed (with delay)
            const folderChildren = relatedFolder.querySelector('.folder-children');
            if (folderChildren && folderChildren.style.display === 'none') {
              if (this.expandTimeout) clearTimeout(this.expandTimeout);

              this.expandTimeout = setTimeout(() => {
                const chevron = relatedFolder.querySelector('.folder-chevron');
                folderChildren.style.display = 'block';
                if (chevron) {
                  chevron.classList.remove('fa-chevron-right');
                  chevron.classList.add('fa-chevron-down');
                }
                relatedFolder.classList.add('folder-open');

                // Lazily initialize sortable for newly expanded folder
                const newContainer = folderChildren.querySelector('[data-sortable-container="true"]');
                if (newContainer && !Sortable.get(newContainer)) {
                  this.initializeSingleContainer(newContainer);
                }
              }, 600); // Delay expansion to avoid accidental opens
            }
          }

          // Prevent dropping folders into their own children
          if (relatedFolder && draggedItem.dataset.type === 'folder') {
            const draggedId = draggedItem.dataset.id;
            const targetFolderId = relatedFolder.dataset.id;
            if (draggedId === targetFolderId || this.isDescendant(draggedId, targetFolderId)) {
              return false;
            }
          }

          if (draggedItem.dataset.type === 'folder' && folderId) {
            const draggedId = draggedItem.dataset.id;
            if (draggedId === folderId || this.isDescendant(draggedId, folderId)) {
              return false;
            }
          }

          return true;
        }
      });

      this.sortables.push(sortable);
    });

    // Restore original display states for folder children
    allFolderChildren.forEach((children, index) => {
      children.style.display = originalDisplayStates[index];
    });
  }

  // Initialize a single container (for lazy loading)
  initializeSingleContainer(container) {
    if (Sortable.get(container)) return; // Already initialized

    const sortable = new Sortable(container, {
      group: {
        name: 'nested-pages',
        put: true,
        pull: true
      },
      animation: 200,
      easing: "cubic-bezier(0.4, 0.0, 0.2, 1)",
      fallbackOnBody: true,
      swapThreshold: 0.5,
      handle: '.page-card-drag-handle',
      ghostClass: 'sortable-ghost',
      chosenClass: 'sortable-chosen',
      dragClass: 'sortable-drag',
      direction: 'vertical',
      removeCloneOnHide: true,
      revertOnSpill: true,
      scroll: true,
      scrollSensitivity: 60,
      scrollSpeed: 15,
      bubbleScroll: true,
      onEnd: (evt) => {
        this.isDragging = false;
        try {
          this.handleDrop(evt);
        } finally {
          this.cleanupDragState();
        }
      }
    });

    this.sortables.push(sortable);
  }

  handleDrop(event) {
    const itemElement = event.item;
    const itemId = itemElement.dataset.id;
    const itemType = itemElement.dataset.type;

    // Check if we dropped directly onto a folder card that was showing drag-over
    const dragOverFolder = this.element.querySelector('.folder-card.drag-over');

    // Validate the drop target
    const sortableContainer = event.to ? event.to.dataset.sortableContainer : null;

    if (!event.to || sortableContainer !== "true") {
      this.reinitializeSortables();
      return;
    }

    // Check if the item actually moved
    const oldFolderId = event.from.dataset.folderId || null;
    const oldIndex = event.oldIndex;
    const newIndex = event.newIndex;

    // Determine new folder
    let newFolderId = event.to.dataset.folderId;
    let newPosition = event.newIndex + 1;

    // If dropped onto a folder card directly (prioritize this over the container's folder_id)
    if (dragOverFolder) {
      const targetFolderId = dragOverFolder.dataset.id;
      // Only use drag-over folder if it's different from where we came from
      if (oldFolderId !== targetFolderId) {
        const folderChildrenContainer = dragOverFolder.querySelector('.page-manager-level[data-sortable-container="true"]');
        if (folderChildrenContainer) {
          const childrenCount = folderChildrenContainer.querySelectorAll('.page-card, .folder-card').length;
          newFolderId = targetFolderId;
          newPosition = childrenCount + 1;
        }
      }
    }

    // Normalize folder IDs
    const normalizedOldFolderId = (!oldFolderId || oldFolderId === 'null' || oldFolderId === '') ? null : oldFolderId;
    const normalizedNewFolderId = (!newFolderId || newFolderId === 'null' || newFolderId === '') ? null : newFolderId;

    // Check if item was dropped in the same position
    if (normalizedOldFolderId === normalizedNewFolderId && oldIndex === newIndex) {
      return;
    }

    // Send update to server
    // Pass the old folder ID too, so we can expand the right folder after reload
    const folderToExpand = normalizedNewFolderId || normalizedOldFolderId;
    this.updateItemPosition(itemId, itemType, normalizedNewFolderId, newPosition, folderToExpand);
  }

  clearDragOverClasses() {
    // Remove drag-over class from all folders
    const folders = this.element.querySelectorAll('.folder-card.drag-over');
    folders.forEach(folder => folder.classList.remove('drag-over'));
  }

  showFolderDropZones() {
    // Show drop zone hint on all folders when dragging
    const folders = this.element.querySelectorAll('.folder-card');
    folders.forEach(folder => folder.classList.add('show-drop-hint'));
  }

  hideFolderDropZones() {
    // Hide drop zone hints
    const folders = this.element.querySelectorAll('.folder-card');
    folders.forEach(folder => folder.classList.remove('show-drop-hint'));
  }

  // Check if targetId is a descendant of folderId
  isDescendant(folderId, targetId) {
    const targetFolder = this.element.querySelector(`.folder-card[data-id="${targetId}"]`);
    if (!targetFolder) return false;

    const folderElement = this.element.querySelector(`.folder-card[data-id="${folderId}"]`);
    if (!folderElement) return false;

    return targetFolder.contains(folderElement);
  }

  updateItemPosition(itemId, itemType, folderId, position, folderToExpand) {
    const csrfToken = document.querySelector('[name="csrf-token"]').content;

    // Show subtle loading indicator
    document.body.classList.add('updating-position');

    // Determine endpoint and payload based on item type
    const endpoint = itemType === 'folder'
      ? `/folders/${itemId}/move`
      : `/page_containers/${itemId}/move`;

    const payload = itemType === 'folder'
      ? {
        position: position,
        parent_id: folderId
      }
      : {
        position: position,
        page_container: {
          folder_id: folderId
        }
      };

    fetch(endpoint, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to update position: ${response.status} ${response.statusText}`);
        }
        return response.json();
      })
      .then(data => {
        // Success - reload and expand all currently open folders
        const openFolders = this.getOpenFolderIds();

        // Make sure the target folder is in the list
        if (folderToExpand && !openFolders.includes(folderToExpand)) {
          openFolders.push(folderToExpand);
        }

        if (openFolders.length > 0) {
          location.href = location.pathname + '?expand_folders=' + openFolders.join(',');
        } else {
          location.reload();
        }
      })
      .catch(error => {
        alert('Failed to move item. Please refresh and try again.');
        location.reload();
      })
      .finally(() => {
        document.body.classList.remove('updating-position');
      });
  }

  // Get IDs of all currently open/expanded folders
  getOpenFolderIds() {
    const openFolders = [];
    const folderCards = this.element.querySelectorAll('.folder-card.folder-open');

    folderCards.forEach(card => {
      const folderId = card.dataset.id;
      if (folderId) {
        openFolders.push(folderId);
      }
    });

    return openFolders;
  }

  // Reinitialize sortables when folders are toggled
  reinitializeSortables() {
    if (this.sortables) {
      this.sortables.forEach(sortable => sortable.destroy());
    }
    this.initializeSortables();
    this.initializeDropdowns();
  }

  // Initialize Bootstrap dropdowns
  initializeDropdowns() {
    if (typeof window.initializeAllDropdowns === 'function') {
      window.initializeAllDropdowns();
    } else if (typeof $ !== 'undefined' && $.fn.dropdown) {
      this.element.querySelectorAll('[data-toggle="dropdown"]').forEach(toggle => {
        const $toggle = $(toggle);
        if ($toggle.data('bs.dropdown')) {
          $toggle.dropdown('dispose');
        }
        $toggle.dropdown();
      });
    }
  }

  // Setup escape key handler to cancel stuck drags
  setupEscapeHandler() {
    this.escapeHandler = (e) => {
      if (e.key === 'Escape' && this.isDragging) {
        this.cleanupDragState();
        this.reinitializeSortables();
      }
    };
    document.addEventListener('keydown', this.escapeHandler);
  }

  // Centralized cleanup method for drag state
  cleanupDragState() {
    document.body.classList.remove('is-dragging');
    this.clearDragOverClasses();
    this.lastHoveredFolder = null;
    if (this.expandTimeout) {
      clearTimeout(this.expandTimeout);
      this.expandTimeout = null;
    }
    if (this.moveTimeout) {
      clearTimeout(this.moveTimeout);
      this.moveTimeout = null;
    }
  }
}

// Make functions globally available for inline event handlers
window.pageManagerController = {
  reinitialize: function () {
    const controller = document.querySelector('[data-controller="page-manager"]');
    if (controller && controller.pageManagerController) {
      controller.pageManagerController.reinitializeSortables();
    }
  }
};

